/**
 * Structured data (schema.org JSON-LD) for the three hub pages: /professional-tools, /corporate-safe-tools and /developers.
 * Every builder takes the same data the page renders, so the markup describes only what a visitor can see (no invented FAQs, no
 * hidden items). Counts follow the owner's rule: a category page shows its own number; a sentence about the whole site uses
 * SITE_FACTS.toolCountPhrase. The existing WebSite/SearchAction block lives in index.html and is not touched here.
 */
import { SITE_FACTS } from "@shared/siteFacts";

export const HUB_SITE_ORIGIN = "https://www.freedailypro.com";
const WEBSITE_REF = { "@type": "WebSite", name: "FreeDailyPro", url: HUB_SITE_ORIGIN } as const;

export interface HubListItem {
  name: string;
  /** Root-relative path such as "/tool/merge-pdf". */
  path: string;
}

function itemList(items: HubListItem[]) {
  return {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: `${HUB_SITE_ORIGIN}${it.path}`,
    })),
  };
}

/** /professional-tools: CollectionPage listing the categories shown on the page, with the page's own count (never the site-wide phrase). */
export function buildProfessionalToolsSchema(args: { count: number; categories: HubListItem[] }): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Professional Tools",
    url: `${HUB_SITE_ORIGIN}/professional-tools`,
    description: `${args.count} professional single-task tools for files, images, video, text, calculators and business documents, grouped by category.`,
    isPartOf: WEBSITE_REF,
    mainEntity: itemList(args.categories),
  };
}

/** /corporate-safe-tools: CollectionPage listing the starter tools the page shows. A whole-site claim uses the approved phrase. */
export function buildCorporateSafeToolsSchema(args: { tools: HubListItem[] }): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Corporate-Safe Tools",
    url: `${HUB_SITE_ORIGIN}/corporate-safe-tools`,
    description: `${SITE_FACTS.toolCountPhrase}. The core file tools run in your browser with no AI model involved, so they work the same under any company AI policy.`,
    isPartOf: WEBSITE_REF,
    mainEntity: itemList(args.tools),
  };
}

export interface HubFaq {
  question: string;
  answer: string;
}

/** FAQPage from the entries the page actually renders. Returns null when there are none (no empty FAQPage). */
export function buildFaqPageSchema(faqs: HubFaq[]): Record<string, unknown> | null {
  if (faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/** /developers: the public REST API and MCP endpoint described on the page. Counts are the page's own floors from SITE_FACTS. */
export function buildDevelopersApiSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebAPI",
    name: "FreeDailyPro API and MCP",
    url: `${HUB_SITE_ORIGIN}/developers`,
    description: `${SITE_FACTS.apiCount} REST endpoints and ${SITE_FACTS.mcpCount} MCP tools for PDF, PowerPoint, document, text, image, video, audio and calculator work, callable with one API key.`,
    documentation: `${HUB_SITE_ORIGIN}/developers`,
    provider: { "@type": "Organization", name: "FreeDailyPro", url: HUB_SITE_ORIGIN },
  };
}
